<template>
  <Menu as="div" class="relative">
    <!-- Translúcido sobre el azul del header, como el resto de sus controles -->
    <MenuButton
      :title="currentCompanyName"
      class="flex h-10 items-center gap-2 rounded-lg bg-white/10 px-3 text-white ring-1 ring-inset ring-white/15 transition-colors hover:bg-white/15 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
    >
      <BuildingOfficeIcon class="h-5 w-5 flex-shrink-0 text-blue-100" />
      <div class="hidden min-w-0 text-left lg:block">
        <p class="max-w-[14rem] truncate text-sm font-medium leading-tight">
          {{ currentCompanyName }}
        </p>
        <p class="text-xs leading-tight text-blue-100">Empresa actual</p>
      </div>
      <ChevronDownIcon class="h-4 w-4 flex-shrink-0 text-blue-100" />
    </MenuButton>

    <transition
      enter-active-class="transition ease-out duration-100"
      enter-from-class="transform opacity-0 scale-95"
      enter-to-class="transform opacity-100 scale-100"
      leave-active-class="transition ease-in duration-75"
      leave-from-class="transform opacity-100 scale-100"
      leave-to-class="transform opacity-0 scale-95"
    >
      <MenuItems class="absolute right-0 mt-2 w-72 origin-top-right rounded-lg bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-50">
        <div class="p-2">
          <div class="px-3 py-2 border-b border-gray-200 mb-2">
            <p class="text-xs font-semibold text-gray-500 uppercase tracking-wide">
              Cambiar empresa
            </p>
          </div>

          <!-- Loading state -->
          <div v-if="companyStore.isLoading" class="px-3 py-4 text-center">
            <LoadingSpinner size="sm" color="primary" class="mx-auto" />
            <p class="text-sm text-gray-500 mt-2">Cargando empresas...</p>
          </div>

          <!-- No companies -->
          <div v-else-if="!companyStore.hasCompanies" class="px-3 py-4 text-center">
            <BuildingOfficeIcon class="w-8 h-8 text-gray-400 mx-auto mb-2" />
            <p class="text-sm text-gray-600">No tienes empresas asignadas</p>
          </div>

          <!-- Companies list -->
          <div v-else class="max-h-64 overflow-y-auto">
            <MenuItem
              v-for="company in companyStore.companies"
              :key="company.id"
              v-slot="{ active }"
            >
              <button
                @click="selectCompany(company.id)"
                :class="[
                  'flex items-center justify-between w-full px-3 py-2 rounded-md transition-colors',
                  active ? 'bg-blue-50' : '',
                  isCurrentCompany(company.id) ? 'bg-blue-100' : ''
                ]"
              >
                <div class="flex items-center space-x-3">
                  <BuildingOfficeIcon
                    :class="[
                      'w-5 h-5',
                      isCurrentCompany(company.id) ? 'text-blue-600' : 'text-gray-400'
                    ]"
                  />
                  <div class="text-left">
                    <p
                      :class="[
                        'text-sm font-medium',
                        isCurrentCompany(company.id) ? 'text-blue-900' : 'text-gray-900'
                      ]"
                    >
                      {{ company.name }}
                    </p>
                    <p class="text-xs text-gray-500">{{ company.code }}</p>
                  </div>
                </div>
                <CheckCircleIcon
                  v-if="isCurrentCompany(company.id)"
                  class="w-5 h-5 text-blue-600"
                />
              </button>
            </MenuItem>
          </div>
        </div>
      </MenuItems>
    </transition>
  </Menu>
</template>

<script setup>
import { computed } from 'vue'
import { Menu, MenuButton, MenuItems, MenuItem } from '@headlessui/vue'
import {
  BuildingOfficeIcon,
  ChevronDownIcon,
  CheckCircleIcon
} from '@heroicons/vue/24/outline'
import { useCompanyContextStore } from '@/stores/companyContext'
import LoadingSpinner from './LoadingSpinner.vue'

const companyStore = useCompanyContextStore()

const currentCompanyName = computed(() => {
  return companyStore.currentCompany?.name || 'Sin empresa'
})

const isCurrentCompany = (companyId) => {
  return companyStore.currentCompanyId === companyId
}

const selectCompany = async (companyId) => {
  if (companyId === companyStore.currentCompanyId) {
    return
  }

  await companyStore.setCurrentCompany(companyId)

  window.location.reload()
}
</script>
